'use client';

import React, { useState } from 'react';
import Image from 'next/image';

interface Marker {
  id: string;
  x: number;
  y: number;
  label: string;
  description: string;
}

const SAMPLE_DATA = {
  title: '1959 Gibson Les Paul Standard',
  year: '1959',
  category: 'Vintage Guitar',
  description: 'An exceptionally rare 1959 Gibson Les Paul Standard in original sunburst finish. This instrument represents the pinnacle of electric guitar design, with its lightweight mahogany body, figured maple top, and PAF humbucking pickups. The guitar has been professionally maintained and retains all original components.',
  collectorNotes: 'Acquired from the estate of a session musician who played on numerous Motown recordings in the 1960s. The wear patterns tell the story of countless studio sessions and live performances.',
  signedBy: ['Eric Clapton', 'Jimmy Page'],
  images: [
  { url: "https://img.rocket.new/generatedImages/rocket_gen_img_19aa54d73-1765753346482.png", alt: 'Front view of 1959 Les Paul' },
  { url: "https://images.unsplash.com/photo-1702859963843-246255e6a65f", alt: 'Headstock detail' },
  { url: "https://images.unsplash.com/photo-1695506976351-d7aa68bb7d67", alt: 'Back view showing grain' }],

  authenticity: {
    authenticated: true,
    inPerson: true,
    signed: true,
    certified: true,
    certifiedBy: 'Gruhn Guitars',
    certificateNumber: 'GG-LP-1959-0847',
    certificationDate: 'March 15, 2023'
  },
  markers: [
  { id: 'm1', x: 45, y: 25, label: '🎸', description: 'Original PAF pickup - Patent Applied For humbucker' },
  { id: 'm2', x: 50, y: 15, label: '🔧', description: 'Kluson tuners - All original from 1959' },
  { id: 'm3', x: 48, y: 45, label: '✨', description: 'Flame maple top - AAA grade figuring' },
  { id: 'm4', x: 52, y: 60, label: '🎚️', description: 'Original potentiometers dated 1959' },
  { id: 'm5', x: 46, y: 75, label: '🔒', description: 'Trapeze tailpiece - Factory original' },
  { id: 'm6', x: 55, y: 35, label: '📝', description: 'Serial number stamped in cavity' }]

};

export default function GalleryMinimalistViewer() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showMarkers, setShowMarkers] = useState(false);
  const [hoveredMarker, setHoveredMarker] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-white">
      {/* Minimal Header */}
      <header className="border-b border-gray-200 py-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">
            {SAMPLE_DATA.category}
          </div>
          <h1 className="text-5xl md:text-6xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontWeight: 300 }}>
            {SAMPLE_DATA.title}
          </h1>
          <div className="text-xl text-gray-600" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
            {SAMPLE_DATA.year}
          </div>
        </div>
      </header>

      {/* Centered Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Main Image - Large and Centered */}
        <div className="mb-12">
          <div className="relative aspect-[3/4] bg-gray-100 mb-6">
            <Image
              src={SAMPLE_DATA.images[currentImageIndex].url}
              alt={SAMPLE_DATA.images[currentImageIndex].alt}
              fill
              className="object-contain" />
            
            
            {/* Subtle Dark Markers */}
            {showMarkers && SAMPLE_DATA.markers.map((marker, idx) =>
            <div
              key={marker.id}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all duration-300 hover:scale-110 z-10"
              style={{
                left: `${marker.x}%`,
                top: `${marker.y}%`,
                animationDelay: `${idx * 0.1}s`
              }}
              onMouseEnter={() => setHoveredMarker(marker.id)}
              onMouseLeave={() => setHoveredMarker(null)}>
              
                <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center text-white shadow-lg border border-gray-600">
                  <span className="text-sm">{marker.label}</span>
                </div>
                
                {/* Elegant Tooltip */}
                {hoveredMarker === marker.id &&
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 w-56 bg-white border border-gray-300 rounded-sm p-4 shadow-xl">
                    <div className="text-sm font-medium text-gray-900 mb-1" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
                      {marker.description.split('-')[0]}
                    </div>
                    <div className="text-xs text-gray-600">{marker.description.split('-')[1]}</div>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-px">
                      <div className="w-2 h-2 bg-white border-r border-b border-gray-300 rotate-45"></div>
                    </div>
                  </div>
              }
              </div>
            )}
          </div>

          {/* Image Caption */}
          <p className="text-center text-sm text-gray-500 italic" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
            {SAMPLE_DATA.images[currentImageIndex].alt}
          </p>
        </div>

        {/* Thumbnail Gallery - Centered */}
        <div className="flex justify-center gap-4 mb-12">
          {SAMPLE_DATA.images.map((img, idx) =>
          <button
            key={idx}
            onClick={() => setCurrentImageIndex(idx)}
            className={`w-24 h-24 bg-gray-100 transition-all duration-300 ${
            currentImageIndex === idx ?
            'ring-1 ring-gray-900 ring-offset-4' : 'opacity-50 hover:opacity-100'}`
            }>
            
              <Image
              src={img.url}
              alt={img.alt}
              width={96}
              height={96}
              className="w-full h-full object-cover" />
            
            </button>
          )}
        </div>

        {/* Markers Toggle */}
        <div className="text-center mb-16">
          <button
            onClick={() => setShowMarkers(!showMarkers)}
            className="px-8 py-3 border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-all duration-300 text-sm uppercase tracking-wider">
            
            {showMarkers ? 'Hide Details' : 'View Details'}
          </button>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 my-12"></div>

        {/* Authenticity Badges - Understated */}
        <div className="mb-12">
          <div className="flex justify-center gap-6 mb-8">
            {SAMPLE_DATA.authenticity.authenticated &&
            <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-green-50 flex items-center justify-center text-green-700 border border-green-200">
                  ✓
                </div>
                <div className="text-xs text-gray-600">Authenticated</div>
              </div>
            }
            {SAMPLE_DATA.authenticity.inPerson &&
            <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-blue-50 flex items-center justify-center text-blue-700 border border-blue-200">
                  ✓
                </div>
                <div className="text-xs text-gray-600">In-Person</div>
              </div>
            }
            {SAMPLE_DATA.authenticity.signed &&
            <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-purple-50 flex items-center justify-center text-purple-700 border border-purple-200">
                  ✓
                </div>
                <div className="text-xs text-gray-600">Signed</div>
              </div>
            }
            {SAMPLE_DATA.authenticity.certified &&
            <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-amber-50 flex items-center justify-center text-amber-700 border border-amber-200">
                  ✓
                </div>
                <div className="text-xs text-gray-600">Certified</div>
              </div>
            }
          </div>

          {/* Certificate Details */}
          {SAMPLE_DATA.authenticity.certified &&
          <div className="text-center text-sm text-gray-600 space-y-1" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif' }}>
              <div>Certified by {SAMPLE_DATA.authenticity.certifiedBy}</div>
              <div className="text-xs text-gray-500">Certificate {SAMPLE_DATA.authenticity.certificateNumber}</div>
              <div className="text-xs text-gray-500">{SAMPLE_DATA.authenticity.certificationDate}</div>
            </div>
          }
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 my-12"></div>

        {/* About Section */}
        <div className="mb-12">
          <h2 className="text-2xl text-gray-900 mb-6 text-center" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontWeight: 400 }}>
            About This Piece
          </h2>
          <p className="text-gray-700 leading-relaxed text-center max-w-2xl mx-auto" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: '17px' }}>
            {SAMPLE_DATA.description}
          </p>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 my-12"></div>

        {/* Signatures - Gold Accent */}
        {SAMPLE_DATA.signedBy.length > 0 &&
        <div className="mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-[#b8945f] mb-6 text-center">
              Signatures
            </div>
            <div className="space-y-3 text-center">
              {SAMPLE_DATA.signedBy.map((name, idx) =>
            <div
              key={idx}
              className="text-3xl text-gray-900"
              style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontWeight: 300, fontStyle: 'italic' }}>
              
                  {name}
                </div>
            )}
            </div>
          </div>
        }

        {/* Divider */}
        <div className="border-t border-gray-200 my-12"></div>

        {/* Collector Notes */}
        <div className="mb-12">
          <h2 className="text-2xl text-gray-900 mb-6 text-center" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontWeight: 400 }}>
            Collector's Notes
          </h2>
          <p className="text-gray-600 leading-relaxed text-center max-w-2xl mx-auto italic" style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: '17px' }}>
            “{SAMPLE_DATA.collectorNotes}”
          </p>
        </div>
      </div>
    </div>);

}